import { useMutation, useQueryClient } from '@tanstack/react-query'
import { App as AntdApp } from 'antd'
import { useTranslation } from 'react-i18next'

import {
  organizationsAccessListQueryKey,
  organizationsTeamUsersCreateMutation,
  organizationsTeamUsersDestroyMutation,
  organizationsTeamUsersListQueryKey
} from 'src/api/main'

import { organizationTeamsQueryKey } from './useOrganizationTeams'

/**
 * Team-membership mutations (add / remove), shared by the organization detail
 * drawer and the user "add to team" action.
 *
 * Every membership change touches three cached surfaces, so each mutation
 * invalidates all of them:
 *  - the teams list (per-unit `users_count`),
 *  - every workspace access-list query (member rows + user detail cards),
 *  - every team-users list (membership ids).
 */
export const useTeamMemberMutations = (workspaceId: number) => {
  const qc = useQueryClient()
  const { message } = AntdApp.useApp()
  const { t } = useTranslation()

  const invalidate = () => {
    void qc.invalidateQueries({
      queryKey: organizationTeamsQueryKey(workspaceId)
    })
    void qc.invalidateQueries({
      queryKey: organizationsAccessListQueryKey({
        query: { organization: workspaceId }
      })
    })
    void qc.invalidateQueries({
      queryKey: organizationsTeamUsersListQueryKey()
    })
  }

  const addMember = useMutation({
    ...organizationsTeamUsersCreateMutation(),
    onSuccess: () => {
      message.success(t('organization.toasts.memberAdded'))
      invalidate()
    },
    onError: () => message.error(t('organization.toasts.memberAddFailed'))
  })

  const removeMember = useMutation({
    ...organizationsTeamUsersDestroyMutation(),
    onSuccess: () => {
      message.success(t('organization.toasts.memberRemoved'))
      invalidate()
    },
    onError: () => message.error(t('organization.toasts.memberRemoveFailed'))
  })

  return { addMember, removeMember }
}
